import { BaseResponse } from '@/types/common';
import axiosInstance from '@/api/axiosInstance';
import dayjs from 'dayjs';
import type {
  ActivityRuleListParams,
  ActivityRuleListResponse,
  ActivityRule,
} from './types';

const baseUrl = `${SUPP_ADMIN_BASE_URL}/supp-management`;

/**
 * 后端返回的活动规则数据格式
 */
interface BackendActivityRule {
  id?: number;
  activityType?: string | number;
  activityTopicEn?: string;
  activityTopicJa?: string;
  descriptionEn?: string;
  descriptionJa?: string;
  startTime?: string;
  endTime?: string;
  prizeSetting?: Array<{ descriptionEn: string; descriptionJa: string }>;
  prizeIssueDesc?: Array<{ descriptionEn: string; descriptionJa: string }>;
  entryRequirement?: Array<{ requirementEn: string; requirementJa: string }>;
  winningCriteria?: Array<{ criterionEn: string; criterionJa: string }>;
  faq?: Array<{ titleEn: string; titleJa: string; contentEn: string; contentJa: string }>;
  importantNotice?: Array<{ noteEn: string; noteJa: string }>;
  isEnabled?: number | boolean;
  createTime?: string;
  updateTime?: string;
}

/**
 * 后端返回的分页响应格式
 */
interface BackendActivityRuleListResponse {
  records?: BackendActivityRule[];
  total?: number;
  size?: number;
  current?: number;
  pages?: number;
}

/**
 * 获取活动规则列表
 * @param params 查询参数
 */
export const getActivityRuleList = async (
  params: ActivityRuleListParams
): Promise<BaseResponse<ActivityRuleListResponse>> => {
  // 所有参数都放在 URL 查询参数中
  const queryParams: {
    pageNum?: number;
    pageSize?: number;
    activityType?: number;
    isEnabled?: number;
    id?: number;
    updateUserName?: string;
  } = {};

  // 页码：优先使用 pageNum，否则使用 currentPage
  if (params.pageNum !== undefined) {
    queryParams.pageNum = params.pageNum;
  } else if (params.currentPage !== undefined) {
    queryParams.pageNum = params.currentPage;
  } else {
    queryParams.pageNum = 1;
  }

  // 每页数量
  if (params.pageSize !== undefined) {
    queryParams.pageSize = params.pageSize;
  } else {
    queryParams.pageSize = 10;
  }

  // 活动类型
  if (params.activityType !== undefined) {
    queryParams.activityType = params.activityType;
  }

  // 是否启用：优先使用 isEnabled，否则从 status 转换（number 类型：1-已启用，0-禁用）
  if (params.isEnabled !== undefined) {
    queryParams.isEnabled = typeof params.isEnabled === 'boolean' 
      ? (params.isEnabled ? 1 : 0) 
      : params.isEnabled;
  } else if (params.status !== undefined) {
    queryParams.isEnabled = params.status;
  }

  // ID 搜索
  if (params.id !== undefined && params.id !== null && params.id !== '') {
    const idNum = typeof params.id === 'string' ? Number(params.id) : params.id;
    if (!isNaN(idNum)) {
      queryParams.id = idNum;
    }
  }

  // 更新人搜索
  if (params.updateUserName !== undefined && params.updateUserName !== null && params.updateUserName !== '') {
    queryParams.updateUserName = params.updateUserName;
  }

  const response = await axiosInstance.get<BaseResponse<BackendActivityRuleListResponse>>(
    `${baseUrl}/ActivityRule/page`,
    {
      params: queryParams,
    }
  );

  // 转换后端数据格式为前端期望的格式
  if (response.data && response.data.data) {
    const backendData = response.data.data;
    
    // 转换记录列表
    const records: ActivityRule[] = (backendData.records || []).map((item) => {
      // 转换 activityType：字符串转数字
      let activityType: number | undefined;
      if (item.activityType !== undefined) {
        if (typeof item.activityType === 'string') {
          // 如果是数字字符串，转换为数字
          const num = Number(item.activityType);
          activityType = isNaN(num) ? undefined : num;
        } else {
          activityType = item.activityType;
        }
      }

      // 转换日期：ISO 字符串转时间戳
      const startTime = item.startTime ? dayjs(item.startTime).valueOf() : undefined;
      const endTime = item.endTime ? dayjs(item.endTime).valueOf() : undefined;
      const createTime = item.createTime ? dayjs(item.createTime).valueOf() : undefined;
      const updateTime = item.updateTime ? dayjs(item.updateTime).valueOf() : undefined;

      // 转换 isEnabled：1/0 或 boolean 转 status 1/0
      const status = item.isEnabled !== undefined 
        ? (typeof item.isEnabled === 'boolean' ? (item.isEnabled ? 1 : 0) : item.isEnabled)
        : undefined;

      return {
        id: item.id,
        activityType,
        activityTopicEn: item.activityTopicEn,
        activityTopicJa: item.activityTopicJa,
        descriptionEn: item.descriptionEn,
        descriptionJa: item.descriptionJa,
        startTime,
        endTime,
        rewardSettings: item.prizeSetting,
        rewardDistribution: item.prizeIssueDesc,
        submissionRequirements: item.entryRequirement,
        awardCriteria: item.winningCriteria,
        commonQuestions: item.faq,
        importantNotes: item.importantNotice,
        status,
        createTime,
      } as ActivityRule;
    });

    // 转换分页信息
    const convertedData: ActivityRuleListResponse = {
      records,
      total: backendData.total,
      currentPage: backendData.current,
      pageSize: backendData.size,
    };

    return {
      ...response.data,
      data: convertedData,
    };
  }

  // 如果数据格式不对，返回空数据
  return {
    status: response.data?.status || 200,
    message: response.data?.message || '数据格式异常',
    data: {
      records: [],
      total: 0,
      currentPage: params.currentPage || 1,
      pageSize: params.pageSize || 10,
    },
  };
};

/**
 * 获取活动规则详情
 * @param id 规则ID
 */
export const getActivityRuleDetail = async (
  id: number
): Promise<BaseResponse<ActivityRule>> => {
  const response = await axiosInstance.get<BaseResponse<BackendActivityRule>>(
    `${baseUrl}/ActivityRule/${id}`
  );

  // 转换后端数据格式为前端期望的格式
  if (response.data && response.data.data) {
    const item = response.data.data;
    
    // 转换 activityType：字符串转数字
    let activityType: number | undefined;
    if (item.activityType !== undefined) {
      if (typeof item.activityType === 'string') {
        const num = Number(item.activityType);
        activityType = isNaN(num) ? undefined : num;
      } else {
        activityType = item.activityType;
      }
    }

    // 转换日期：ISO 字符串转时间戳，如果已经是时间戳则直接使用
    const startTime = item.startTime 
      ? (typeof item.startTime === 'string' ? dayjs(item.startTime).valueOf() : item.startTime)
      : undefined;
    const endTime = item.endTime 
      ? (typeof item.endTime === 'string' ? dayjs(item.endTime).valueOf() : item.endTime)
      : undefined;
    const createTime = item.createTime 
      ? (typeof item.createTime === 'string' ? dayjs(item.createTime).valueOf() : item.createTime)
      : undefined;
    const updateTime = item.updateTime 
      ? (typeof item.updateTime === 'string' ? dayjs(item.updateTime).valueOf() : item.updateTime)
      : undefined;

    // 转换 isEnabled：1/0 或 boolean 转 status 1/0
    const status = item.isEnabled !== undefined 
      ? (typeof item.isEnabled === 'boolean' ? (item.isEnabled ? 1 : 0) : item.isEnabled)
      : undefined;

    const convertedData: ActivityRule = {
      id: item.id,
      activityType,
      activityTopicEn: item.activityTopicEn,
      activityTopicJa: item.activityTopicJa,
      descriptionEn: item.descriptionEn,
      descriptionJa: item.descriptionJa,
      startTime,
      endTime,
      rewardSettings: item.prizeSetting,
      rewardDistribution: item.prizeIssueDesc,
      submissionRequirements: item.entryRequirement,
      awardCriteria: item.winningCriteria,
      commonQuestions: item.faq,
      importantNotes: item.importantNotice,
      status,
      createTime,
    };

    return {
      ...response.data,
      data: convertedData,
    };
  }

  // 如果数据格式不对，返回错误
  return {
    status: response.data?.status || 200,
    message: response.data?.message || '获取详情失败',
    data: {} as ActivityRule,
  };
};

/**
 * 创建活动规则
 * @param data 活动规则数据
 */
export const createActivityRule = async (
  data: ActivityRule
): Promise<BaseResponse<ActivityRule>> => {
  // 转换前端数据格式为后端需要的格式
  const requestData = {
    id: data.id,
    activityType: data.activityType,
    activityTopicEn: data.activityTopicEn,
    activityTopicJa: data.activityTopicJa,
    descriptionEn: data.descriptionEn,
    descriptionJa: data.descriptionJa,
    startTime: data.startTime,
    endTime: data.endTime,
    prizeSetting: data.rewardSettings ? data.rewardSettings : undefined,
    prizeIssueDesc: data.rewardDistribution ? data.rewardDistribution : undefined,
    entryRequirement: data.submissionRequirements ? data.submissionRequirements : undefined,
    winningCriteria: data.awardCriteria ? data.awardCriteria : undefined,
    faq: data.commonQuestions ? data.commonQuestions : undefined,
    importantNotice: data.importantNotes ? data.importantNotes : undefined,
    isEnabled: data.status,
    createTime: data.createTime,
    updateTime: data.updateTime,
  };

  const response = await axiosInstance.post<BaseResponse<ActivityRule>>(
    `${baseUrl}/ActivityRule/add`,
    requestData
  );
  
  return response.data;
};

/**
 * 更新活动规则
 * @param data 活动规则数据（必须包含 id）
 */
export const updateActivityRule = async (
  data: ActivityRule
): Promise<BaseResponse<ActivityRule>> => {
  // 确保有 id
  if (!data.id) {
    throw new Error('更新活动规则需要提供 id');
  }

  // 转换前端数据格式为后端需要的格式（和新增接口一致，但必须包含 id）
  const requestData = {
    id: data.id, // 必须包含当前 id
    activityType: data.activityType,
    activityTopicEn: data.activityTopicEn,
    activityTopicJa: data.activityTopicJa,
    descriptionEn: data.descriptionEn,
    descriptionJa: data.descriptionJa,
    startTime: data.startTime,
    endTime: data.endTime,
    prizeSetting: data.rewardSettings ? data.rewardSettings : undefined,
    prizeIssueDesc: data.rewardDistribution ? data.rewardDistribution : undefined,
    entryRequirement: data.submissionRequirements ? data.submissionRequirements : undefined,
    winningCriteria: data.awardCriteria ? data.awardCriteria : undefined,
    faq: data.commonQuestions ? data.commonQuestions : undefined,
    importantNotice: data.importantNotes ? data.importantNotes : undefined,
    isEnabled: data.status,
    createTime: data.createTime,
    updateTime: data.updateTime,
  };

  const response = await axiosInstance.post<BaseResponse<ActivityRule>>(
    `${baseUrl}/ActivityRule/update`,
    requestData
  );
  
  return response.data;
};

/**
 * 删除活动规则
 * @param id 规则ID
 */
export const deleteActivityRule = async (
  id: number
): Promise<BaseResponse<void>> => {
  const response = await axiosInstance.get<BaseResponse<void>>(
    `${baseUrl}/ActivityRule/delete/${id}`
  );
  
  return response.data;
};

